import { cloneDeep } from 'lodash';
import { IObservable, Subject } from '~/rage/utils';
import { IClientConfigs, Job, numWord } from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { INotificationsService } from '../NotificationsService';
import { VarsService } from '../VarsService';
import {
	CharacterJobTasks,
	IJobTask,
	IJobTaskClientData,
	IJobTasksService,
	JobTaskData,
} from './jobTasksServiceTypes';

export class JobTasksService implements IJobTasksService {
	private tasksByJob = new Map<Job, Subject<IJobTask[]>>();
	private tasksExpireAt = new Subject<number>(0);

	private initialTasks = new Subject<IJobTask[]>([]);
	private jobTasks: Map<Job, JobTaskData> = new Map();

	constructor(
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
		private readonly notifications: INotificationsService,
		private readonly vars: VarsService,
	) {
		this.gameConfig
			.getConfigs()
			.subscribe((configs) => this.handleGameConfigChange(configs));

		this.backend.addEventHandler(
			'jobTasks:completed',
			(payload: { jobId: Job; taskId: string }) => {
				this.updateTaskProgress(payload.jobId, payload.taskId);

				const task = this.findTaskByJobAndId(payload.jobId, payload.taskId);
				if (task) {
					this.notifications.showSuccess(
						`Задание работы "${task.name}" выполнено.`,
					);
				}
			},
		);

		this.backend.addEventHandler(
			'jobTasks:progress',
			(payload: { jobId: Job; taskId: string; count: number }) => {
				this.updateTaskProgress(payload.jobId, payload.taskId, payload.count);
			},
		);

		this.backend.addEventHandler(
			'jobTasks:updateTasks',
			(payload: CharacterJobTasks) => {
				this.tasksExpireAt.next(payload.expiresAt);
				this.jobTasks = new Map(Object.entries(payload.tasks)) as Map<
					Job,
					JobTaskData
				>;
				this.updateTasksFromServer();
			},
		);
	}

	private handleGameConfigChange(configs: Partial<IClientConfigs>): void {
		if (!configs.jobTasks) return;

		this.updateTasksFromServer();
	}

	public getJobTasks(jobId: Job): IObservable<IJobTask[]> {
		return this.getTasksByJob(jobId);
	}

	public getActiveJobTasks(): IObservable<IJobTask[]> {
		const activeJobId = this.vars
			.get<Job | null>('activeJobId', null)
			.getValue();

		if (!activeJobId) {
			return this.initialTasks;
		}

		return this.getTasksByJob(activeJobId);
	}

	public getTasksExpireAt(): IObservable<number> {
		return this.tasksExpireAt;
	}

	private getTasksByJob(jobId: Job): IObservable<IJobTask[]> {
		let tasks = this.tasksByJob.get(jobId);

		if (!tasks) {
			tasks = new Subject<IJobTask[]>([]);

			this.tasksByJob.set(jobId, tasks);
		}

		return tasks;
	}

	private updateTasksFromServer() {
		const jobTasksConfig = this.gameConfig.configs?.jobTasks;

		if (!jobTasksConfig || jobTasksConfig.targets.length === 0) {
			return;
		}

		// Map target configs by job and target id for quick lookup
		const targetsByJobAndId = new Map<
			string,
			IClientConfigs['jobTasks']['targets'][number]
		>();
		for (const target of jobTasksConfig.targets) {
			const key = `${target.jobId}_${target.id}`;
			targetsByJobAndId.set(key, target);
		}

		// Map task configs by task id for quick lookup
		const tasksByTaskId = new Map<
			string,
			IClientConfigs['jobTasks']['tasks'][number]
		>();
		for (const task of jobTasksConfig.tasks) {
			tasksByTaskId.set(task.taskId, task);
		}

		// targetIdByTaskId from gameConfig maps taskId to targetId
		const targetIdByTaskId: Record<string, string> =
			jobTasksConfig.targetIdByTaskId;

		for (const [jobId, tasks] of this.jobTasks) {
			const mergedTasks: IJobTaskClientData[] = [];

			for (const task of tasks) {
				if (!task) {
					continue;
				}

				mergedTasks.push(task);
			}

			const newTasks: IJobTask[] = [];
			for (const task of mergedTasks) {
				const taskId = String(task.taskId);
				const targetId = targetIdByTaskId?.[taskId] ?? '';
				const targetKey = `${jobId}_${targetId}`;
				const targetConfig = targetsByJobAndId.get(targetKey);
				const taskConfig = tasksByTaskId.get(taskId);

				if (!targetConfig || !taskConfig) {
					continue;
				}

				newTasks.push(this.createTaskFromData(task, targetConfig, taskConfig));
			}

			newTasks.sort((a, b) => a.targetCount - b.targetCount);

			let tasksSubject = this.tasksByJob.get(jobId);

			if (!tasksSubject) {
				tasksSubject = new Subject<IJobTask[]>(newTasks);

				this.tasksByJob.set(jobId, tasksSubject);
			} else {
				tasksSubject.next(newTasks);
			}
		}
	}

	private updateTaskProgress(jobId: Job, taskId: string, newProgress?: number) {
		const tasksSubject = this.tasksByJob.get(jobId);
		if (!tasksSubject) {
			return;
		}

		const task = tasksSubject.getValue().find((t) => t.id === taskId);
		if (!task) {
			return;
		}

		const updatedTasks = cloneDeep(tasksSubject.getValue());
		const updatedTask = updatedTasks.find((t) => t.id === taskId);

		if (updatedTask) {
			updatedTask.currentCount = newProgress
				? newProgress
				: updatedTask.targetCount;
		}

		tasksSubject.next(updatedTasks);
	}

	private findTaskByJobAndId(jobId: Job, taskId: string): IJobTask | null {
		const tasksSubject = this.tasksByJob.get(jobId);

		if (!tasksSubject) {
			return null;
		}

		return tasksSubject.getValue().find((t) => t.id === taskId) ?? null;
	}

	private createTaskFromData(
		data: IJobTaskClientData,
		targetConfig: IClientConfigs['jobTasks']['targets'][number],
		taskConfig: IClientConfigs['jobTasks']['tasks'][number],
	): IJobTask {
		let name = `Задание ???`;
		let description = `Без описания (${data.progress ?? 0}/${
			taskConfig.targetCount
		})`;

		if (targetConfig) {
			name = targetConfig.title;

			let word = '';
			if (targetConfig.wordForms?.length === 3) {
				word = numWord(taskConfig.targetCount, targetConfig.wordForms);
			}

			description = targetConfig.text
				.replace('{count}', taskConfig.targetCount.toString())
				.replace('{word}', word);
		}

		return {
			id: data.taskId,
			jobId: taskConfig.jobId,
			name,
			description,
			currentCount: data.progress ?? 0,
			targetCount: taskConfig.targetCount,
			rewardMoney: taskConfig.rewardMoney,
			rewardExp: taskConfig.rewardExp,
		};
	}
}
